import { useOrgChart } from './useOrgChart';
import { getPaletteColor } from '@/core/lib/colors';
import type { OrgChartAreaNodeDto, OrgChartMemberNodeDto } from '../types';
import type { TreeNodeDef } from '@/shared/components/custom/tree-chart';

// ─── Node data shapes ─────────────────────────────────────────────────────────

export interface OrgRootNodeData extends Record<string, unknown> {
  orgName: string;
  memberCount: number;
  projectCount: number;
  /** Omitted in the "Proyectos" tab — creating an area doesn't apply there. */
  onCreateArea?: () => void;
}

export interface OrgAreaNodeData extends Record<string, unknown> {
  areaId: string;
  name: string | null;
  color: string | null;
  icon: string | null;
  childAreaCount: number;
  hasChildren: boolean;
  memberCount: number;
}

export interface OrgMemberNodeData extends Record<string, unknown> {
  memberId: string;
  /** Tenant-level member id — required by `GET .../org-chart/members/{id}/summary`. */
  organizationMemberId: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  jobTitle: string | null;
  completedTasksCount: number;
  totalTasksCount: number;
  dimmed: boolean;
}

// ─── Node dimensions ──────────────────────────────────────────────────────────

export const NODE_WIDTHS: Record<string, number> = {
  'org-root': 200,
  'org-area': 200,
  'org-member': 180,
  'org-project': 220,
};

export const NODE_HEIGHTS: Record<string, number> = {
  'org-root': 88,
  'org-area': 80,
  'org-member': 148,
  'org-project': 96,
};

// ─── Tree flattening ─────────────────────────────────────────────────────────

export function memberNode(
  member: OrgChartMemberNodeDto & { id: string },
  parentId: string,
  search: string,
): TreeNodeDef {
  return {
    id: `member-${member.id}`,
    parentId,
    type: 'org-member',
    width: NODE_WIDTHS['org-member'],
    height: NODE_HEIGHTS['org-member'],
    data: {
      memberId: member.id,
      organizationMemberId: member.organizationMemberId,
      displayName: member.displayName,
      avatarUrl: member.avatarUrl,
      jobTitle: member.jobTitle,
      completedTasksCount: member.completedTasksCount,
      totalTasksCount: member.totalTasksCount,
      dimmed: search.length > 0 && !(member.displayName ?? '').toLowerCase().includes(search),
    } satisfies OrgMemberNodeData,
  };
}

export function flattenAreas(
  areas: OrgChartAreaNodeDto[],
  parentId: string,
  search: string,
): TreeNodeDef[] {
  const nodes: TreeNodeDef[] = [];

  for (const area of areas) {
    if (!area.id) continue;

    const childAreas = area.children ?? [];
    const members = area.members ?? [];
    const colorValue = getPaletteColor(area.color ?? '')?.value;

    nodes.push({
      id: area.id,
      parentId,
      type: 'org-area',
      width: NODE_WIDTHS['org-area'],
      height: NODE_HEIGHTS['org-area'],
      edgeStyle: colorValue ? { stroke: colorValue, strokeWidth: 1.5, opacity: 0.8 } : undefined,
      data: {
        areaId: area.id,
        name: area.name,
        color: area.color,
        icon: area.icon,
        childAreaCount: childAreas.length,
        hasChildren: childAreas.length > 0 || members.length > 0,
        memberCount: members.length,
      } satisfies OrgAreaNodeData,
    });

    for (const member of members.filter(
      (m): m is OrgChartMemberNodeDto & { id: string } => m.id !== null,
    )) {
      nodes.push(memberNode(member, area.id, search));
    }

    nodes.push(...flattenAreas(childAreas, area.id, search));
  }

  return nodes;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

interface OrgChartDataResult {
  nodes: TreeNodeDef[];
  isLoading: boolean;
  error: Error | null;
  totalMembers: number;
}

export function useOrgChartData(
  searchQuery: string,
  onCreateArea: () => void,
  enabled = true,
): OrgChartDataResult {
  const { data, isLoading, error } = useOrgChart(enabled);

  if (isLoading || !data) {
    return { nodes: [], isLoading, error: error as Error | null, totalMembers: 0 };
  }

  const areas = data.areas ?? [];
  const unassignedMembers = data.unassignedMembers ?? [];

  if (areas.length === 0 && unassignedMembers.length === 0) {
    return { nodes: [], isLoading: false, error: null, totalMembers: data.totalMembers };
  }

  const search = searchQuery.trim().toLowerCase();
  const nodeDefs: TreeNodeDef[] = [];

  nodeDefs.push({
    id: 'root',
    parentId: null,
    type: 'org-root',
    width: NODE_WIDTHS['org-root'],
    height: NODE_HEIGHTS['org-root'],
    data: {
      orgName: data.tenantName ?? '',
      memberCount: data.totalMembers,
      projectCount: data.totalProjects,
      onCreateArea,
    } satisfies OrgRootNodeData,
  });

  nodeDefs.push(...flattenAreas(areas, 'root', search));

  for (const member of unassignedMembers.filter(
    (m): m is OrgChartMemberNodeDto & { id: string } => m.id !== null,
  )) {
    nodeDefs.push(memberNode(member, 'root', search));
  }

  return {
    nodes: nodeDefs,
    isLoading: false,
    error: null,
    totalMembers: data.totalMembers,
  };
}
